// src/Utils/debug.js
// Environment-aware debug logging utility
// Only logs in development mode unless explicitly enabled

const isDev = process.env.NODE_ENV === "development";
const debugEnabled = process.env.REACT_APP_DEBUG_MODE === "true";

// Check if logging should be active
const shouldLog = () => isDev || debugEnabled;

/**
 * Debug logging utility
 * All methods only output in development mode
 */
export const debug = {
  /**
   * General debug log (most verbose)
   */
  log: (...args) => {
    if (shouldLog()) {
      console.log("[DEBUG]", ...args);
    }
  },

  /**
   * Info level - important flow information
   */
  info: (...args) => {
    if (shouldLog()) {
      console.info("[INFO]", ...args);
    }
  },

  /**
   * Warning level - potential issues
   */
  warn: (...args) => {
    if (shouldLog()) {
      console.warn("[WARN]", ...args);
    }
  },

  /**
   * Error level - always logs (errors should be visible)
   */
  error: (...args) => {
    console.error("[ERROR]", ...args);
  },

  /**
   * Auth-specific debug logs
   */
  auth: (...args) => {
    if (shouldLog()) {
      console.log("[AUTH]", ...args);
    }
  },

  /**
   * API/Service debug logs
   */
  api: (...args) => {
    if (shouldLog()) {
      console.log("[API]", ...args);
    }
  },

  /**
   * Component lifecycle debug logs
   */
  component: (...args) => {
    if (shouldLog()) {
      console.log("[COMPONENT]", ...args);
    }
  },

  /**
   * Group related logs together
   */
  group: (label, fn) => {
    if (shouldLog()) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  },

  /**
   * Log with timing information
   */
  time: (label) => {
    if (shouldLog()) {
      console.time(label);
    }
  },

  timeEnd: (label) => {
    if (shouldLog()) {
      console.timeEnd(label);
    }
  },

  /**
   * Table format for objects/arrays
   */
  table: (data) => {
    if (shouldLog()) {
      console.table(data);
    }
  },
};

export default debug;
